// src/metadata/context.tsx
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { buildOrganization, buildWebSite } from './json-ld';
import { SITE_URL } from './site-url';

export interface HreflangAlternate {
  hreflang: string;
  href: string;
}

// Define the structure of your metadata based on your example
export interface Metadata {
  title?: string;
  description?: string;
  twitter?: {
    card?: string;
    title?: string;
    description?: string;
    site?: string; // site account (matches the edge middleware)
    creator?: string;
    image?: string; // Added for consistency
  };
  metadataBase?: URL | string; // Allow string for easier usage
  openGraph?: {
    type?: string; // Common OG type
    title?: string;
    description?: string;
    images?: string[];
    /** Describes `images[0]`; size only when known (the default share card). */
    imageAlt?: string;
    imageWidth?: number;
    imageHeight?: number;
    url?: string | URL; // Common OG url
    siteName?: string;
  };
  // SEO extensions
  canonical?: string;
  alternates?: HreflangAlternate[];
  robots?: string;
  ogLocale?: string;
  ogLocaleAlternate?: string[];
  keywords?: string[];
  jsonLd?: object[];
}

interface MetadataContextProps {
  metadata: Metadata;
  setMetadata: (newMetadata: Partial<Metadata>) => void;
}

// Create the context with a default value (can be empty or initial defaults)
const MetadataContext = createContext<MetadataContextProps | undefined>(undefined);

// Define the default metadata
const defaultMetadata: Metadata = {
  title: 'Flashka - Learn faster with AI Flashcards!',
  description:
    'Flashka turns your notes, PDFs, and slides into high-quality AI flashcards, quizzes, and spaced-repetition study. Learn smarter and remember more — used by 2M+ students in 100+ countries.',
  twitter: {
    card: 'summary_large_image', // Default twitter card type
    title: 'Flashka - Learn faster with AI Flashcards!',
    description:
      'Flashka turns your notes, PDFs, and slides into high-quality AI flashcards, quizzes, and spaced-repetition study.',
    site: '@SteDjokovic',
    creator: '@SteDjokovic',
    image: '/social-thumbnail.png', // Consistent image
  },
  metadataBase: new URL(`${SITE_URL}/`),
  openGraph: {
    type: 'website',
    title: 'Flashka - Learn faster with AI Flashcards!',
    description:
      'Flashka turns your notes, PDFs, and slides into high-quality AI flashcards, quizzes, and spaced-repetition study.',
    images: ['/social-thumbnail.png'],
    url: `${SITE_URL}/`,
    siteName: 'Flashka',
  },
  canonical: `${SITE_URL}/`,
  robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  ogLocale: 'en_US',
};

// Create the provider component
export const Metadata = ({ children }: { children: ReactNode }) => {
  const [metadata, setMetadataState] = useState<Metadata>(defaultMetadata);

  // Function to merge new metadata with existing, allowing partial updates.
  // `useCallback` + the `useMemo` below are NOT cosmetics: this provider wraps
  // the ENTIRE routed tree, and an unmemoized value gives every consumer a new
  // context on every render of this component — which React then propagates
  // past every bailing fiber below it. Same fix as the app's root providers.
  const setMetadata = useCallback((newMetadata: Partial<Metadata>) => {
    setMetadataState((prevMetadata) => {
      // Deep merge for nested objects like twitter and openGraph
      const updatedMetadata = { ...prevMetadata };
      if (newMetadata.title !== undefined) updatedMetadata.title = newMetadata.title;
      if (newMetadata.description !== undefined)
        updatedMetadata.description = newMetadata.description;
      if (newMetadata.metadataBase !== undefined)
        updatedMetadata.metadataBase = newMetadata.metadataBase;
      if (newMetadata.canonical !== undefined)
        updatedMetadata.canonical = newMetadata.canonical;
      if (newMetadata.alternates !== undefined)
        updatedMetadata.alternates = newMetadata.alternates;
      if (newMetadata.robots !== undefined) updatedMetadata.robots = newMetadata.robots;
      if (newMetadata.ogLocale !== undefined)
        updatedMetadata.ogLocale = newMetadata.ogLocale;
      if (newMetadata.ogLocaleAlternate !== undefined)
        updatedMetadata.ogLocaleAlternate = newMetadata.ogLocaleAlternate;
      if (newMetadata.keywords !== undefined)
        updatedMetadata.keywords = newMetadata.keywords;
      if (newMetadata.jsonLd !== undefined) updatedMetadata.jsonLd = newMetadata.jsonLd;

      if (newMetadata.twitter) {
        updatedMetadata.twitter = {
          ...prevMetadata.twitter,
          ...newMetadata.twitter,
        };
      }
      if (newMetadata.openGraph) {
        updatedMetadata.openGraph = {
          ...prevMetadata.openGraph,
          ...newMetadata.openGraph,
        };
      }
      return updatedMetadata;
    });
  }, []);

  const value = useMemo(() => ({ metadata, setMetadata }), [metadata, setMetadata]);

  return (
    <MetadataContext.Provider value={value}>
      <MetadataRenderer />
      {children}
    </MetadataContext.Provider>
  );
};

export const MetadataRenderer = () => {
  const { metadata } = useMetadata();
  const baseUrl = metadata.metadataBase?.toString() || '';

  // Helper to construct absolute URLs
  const absoluteUrl = (path?: string) => {
    if (!path) return undefined;
    try {
      // If it's already an absolute URL, return it
      new URL(path);
      return path;
    } catch {
      // Otherwise, construct it using the base URL
      // Ensure no double slashes if path starts with / and baseUrl ends with /
      return `${baseUrl.replace(/\/$/, '')}${path.startsWith('/') ? '' : '/'}${path}`;
    }
  };

  // Site-wide structured data (rendered on every page). Google reads ld+json
  // anywhere in the document, so body placement is fine.
  const siteOrigin = baseUrl.replace(/\/$/, '');
  const siteJsonLd = [buildOrganization(siteOrigin), buildWebSite(siteOrigin)];

  return (
    <>
      {/* Basic metadata */}
      {metadata.title && <title>{metadata.title}</title>}
      {metadata.description && <meta name="description" content={metadata.description} />}
      {metadata.keywords && metadata.keywords.length > 0 && (
        <meta name="keywords" content={metadata.keywords.join(', ')} />
      )}
      {metadata.robots && <meta name="robots" content={metadata.robots} />}

      {/* Canonical + hreflang alternates */}
      {metadata.canonical && <link rel="canonical" href={metadata.canonical} />}
      {metadata.alternates?.map((alt) => (
        <link
          key={`alt-${alt.hreflang}`}
          rel="alternate"
          hrefLang={alt.hreflang}
          href={alt.href}
        />
      ))}

      {/* OpenGraph metadata */}
      {metadata.openGraph?.type && (
        <meta property="og:type" content={metadata.openGraph.type} />
      )}
      {metadata.openGraph?.siteName && (
        <meta property="og:site_name" content={metadata.openGraph.siteName} />
      )}
      {metadata.openGraph?.title && (
        <meta property="og:title" content={metadata.openGraph.title} />
      )}
      {metadata.openGraph?.description && (
        <meta property="og:description" content={metadata.openGraph.description} />
      )}
      {metadata.openGraph?.url && (
        <meta
          property="og:url"
          content={absoluteUrl(metadata.openGraph.url.toString())}
        />
      )}
      {metadata.openGraph?.images?.map((img, index) => (
        <meta property="og:image" content={absoluteUrl(img)} key={`og-image-${index}`} />
      ))}
      {metadata.openGraph?.imageWidth && (
        <meta property="og:image:width" content={String(metadata.openGraph.imageWidth)} />
      )}
      {metadata.openGraph?.imageHeight && (
        <meta
          property="og:image:height"
          content={String(metadata.openGraph.imageHeight)}
        />
      )}
      {metadata.openGraph?.imageAlt && (
        <meta property="og:image:alt" content={metadata.openGraph.imageAlt} />
      )}
      {metadata.ogLocale && <meta property="og:locale" content={metadata.ogLocale} />}
      {metadata.ogLocaleAlternate?.map((locale) => (
        <meta
          property="og:locale:alternate"
          content={locale}
          key={`og-locale-${locale}`}
        />
      ))}

      {/* Twitter metadata */}
      {metadata.twitter?.card && (
        <meta name="twitter:card" content={metadata.twitter.card} />
      )}
      {metadata.twitter?.title && (
        <meta name="twitter:title" content={metadata.twitter.title} />
      )}
      {metadata.twitter?.description && (
        <meta name="twitter:description" content={metadata.twitter.description} />
      )}
      {metadata.twitter?.site && (
        <meta name="twitter:site" content={metadata.twitter.site} />
      )}
      {metadata.twitter?.creator && (
        <meta name="twitter:creator" content={metadata.twitter.creator} />
      )}
      {metadata.twitter?.image && (
        <meta name="twitter:image" content={absoluteUrl(metadata.twitter.image)} />
      )}
      {metadata.openGraph?.imageAlt && (
        <meta name="twitter:image:alt" content={metadata.openGraph.imageAlt} />
      )}

      {/* Structured data: site-wide + page-specific */}
      {siteJsonLd.map((graph, index) => (
        <script
          key={`site-jsonld-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(graph) }}
        />
      ))}
      {metadata.jsonLd?.map((graph, index) => (
        <script
          key={`page-jsonld-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(graph) }}
        />
      ))}
    </>
  );
};

// Create a custom hook for easy access to the context

export const useMetadata = (): MetadataContextProps => {
  const context = useContext(MetadataContext);
  if (context === undefined) {
    throw new Error('useMetadata must be used within a MetadataProvider');
  }
  return context;
};
